import React, { useState, useEffect, useRef, useMemo } from 'react';
import "./PostMedia.scss";
import Hls from 'hls.js';

const PostMedia = ({ post, postId, activeIndex, onCarousalButtonClick }) => {
    const mediaKeys = useMemo(() => (post.media ? Object.keys(post.media) : []), [post.media]);
    const [expandedMedia, setExpandedMedia] = useState(null);
    const [expandedIndex, setExpandedIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    
    // Store HLS instances for cleanup
    const hlsInstances = useRef({});
    
    // Touch handling
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [isSwiping, setIsSwiping] = useState(false);
    
    // Video refs
    const videoRefs = useRef({});
    const expandedVideoRef = useRef(null);
    
    // Detect mobile devices
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Helper to check if URL is HLS
    const isHlsUrl = (url) => {
        return url && (url.endsWith('.m3u8') || url.includes('.m3u8?'));
    };

    // Initialize HLS for a video element
    const initializeHls = (videoElement, url, mediaId) => {
        if (!videoElement || !isHlsUrl(url)) return;
        
        // Destroy existing instance first
        if (hlsInstances.current[mediaId]) {
            hlsInstances.current[mediaId].destroy();
            delete hlsInstances.current[mediaId];
        }
        
        if (Hls.isSupported()) {
            const hls = new Hls({ 
                enableWorker: true,
                lowLatencyMode: true
            });
            
            hls.loadSource(url);
            hls.attachMedia(videoElement);
            
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                if (videoElement.autoPlay) {
                    videoElement.play().catch(e => console.error('Error auto-playing video:', e));
                }
            });
            
            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            hls.startLoad();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError();
                            break;
                        default:
                            console.error('Fatal HLS error:', data);
                            if (hlsInstances.current[mediaId]) {
                                hlsInstances.current[mediaId].destroy();
                                delete hlsInstances.current[mediaId];
                            }
                            break;
                    }
                }
            });
            
            hlsInstances.current[mediaId] = hls;
        } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari native HLS support
            videoElement.src = url;
        }
    };

    // Destroy HLS instance
    const destroyHls = (mediaId) => {
        if (hlsInstances.current[mediaId]) {
            hlsInstances.current[mediaId].destroy();
            delete hlsInstances.current[mediaId];
        }
    };

    const handleMediaClick = (mediaId, index, e) => {
        // Pause all in-post videos
        Object.entries(videoRefs.current).forEach(([id, video]) => {
            if (video && !video.paused) {
                video.pause();
                video.currentTime = 0;
            }
        });
        
        if (!isSwiping) {
            setExpandedMedia(mediaId);
            setExpandedIndex(index);
        }
    };

    const closeExpandedView = (e) => {
        e.stopPropagation();
        
        if (expandedVideoRef.current) {
            expandedVideoRef.current.pause();
            expandedVideoRef.current.currentTime = 0;
            
            // Destroy HLS if needed
            if (expandedMedia && isHlsUrl(post.media[expandedMedia]?.url)) {
                destroyHls(`expanded-${expandedMedia}`);
            }
            
            expandedVideoRef.current.src = "";
            expandedVideoRef.current.load();
        }
        
        setExpandedMedia(null);
    };

    const navigateExpandedMedia = (direction, e) => {
        if (e) e.stopPropagation();

        if (expandedVideoRef.current) {
            expandedVideoRef.current.pause();
            expandedVideoRef.current.currentTime = 0;
        }
        
        // Destroy current HLS instance
        if (expandedMedia && isHlsUrl(post.media[expandedMedia]?.url)) {
            destroyHls(`expanded-${expandedMedia}`);
        }
        
        const newIndex = direction === 'next'
            ? Math.min(expandedIndex + 1, mediaKeys?.length - 1)
            : Math.max(expandedIndex - 1, 0);

        setExpandedIndex(newIndex);
        setExpandedMedia(mediaKeys[newIndex]);
    };

    // Touch handlers
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
        setTouchEnd(null);
        setIsSwiping(false);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
        if (touchStart && Math.abs(touchStart - e.targetTouches[0].clientX) > 10) {
            setIsSwiping(true);
        }
    };

    const handleTouchEnd = (inExpandedView = true) => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;
        
        if (inExpandedView) {
            if (isLeftSwipe && expandedIndex < mediaKeys?.length - 1) {
                navigateExpandedMedia('next');
            } else if (isRightSwipe && expandedIndex > 0) {
                navigateExpandedMedia('prev');
            }
        } else {
            if (isLeftSwipe && activeIndex < mediaKeys?.length - 1) {
                onCarousalButtonClick(postId, mediaKeys, "next");
            } else if (isRightSwipe && activeIndex > 0) {
                onCarousalButtonClick(postId, mediaKeys, "prev");
            }
        }
        
        setTouchStart(null);
        setTouchEnd(null);
        setIsSwiping(false);
    };

    // Setup intersection observer and lazy-load HLS for active carousel item
    useEffect(() => {
        const observers = [];
        
        mediaKeys.forEach((mediaId, index) => {
            const media = post.media[mediaId];
            if (media.type === 'video' && videoRefs.current[mediaId]) {
                const videoElement = videoRefs.current[mediaId];
                
                // If this is the active video and it's HLS, initialize it
                if (index === activeIndex && isHlsUrl(media.url) && !hlsInstances.current[mediaId]) {
                    // Small delay to ensure DOM is ready
                    setTimeout(() => {
                        initializeHls(videoElement, media.url, mediaId);
                    }, 100);
                }
                
                const observer = new IntersectionObserver(
                    ([entry]) => {
                        if (!entry.isIntersecting && !expandedMedia) {
                            videoElement.pause();
                        }
                    },
                    { threshold: 0.1 }
                );
                
                observer.observe(videoElement);
                observers.push(observer);
            }
        });
        
        return () => {
            observers.forEach((observer) => observer.disconnect());
        };
    }, [mediaKeys, expandedMedia, post.media, activeIndex]);

    // Prevent background scroll when expanded
    useEffect(() => {
        if (expandedMedia) {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalOverflow;
            };
        }
    }, [expandedMedia]);

    // Disable controls on regular videos when expanded mode is active
    useEffect(() => {
        if (expandedMedia) {
            Object.values(videoRefs.current).forEach(video => {
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                    video.controls = false;
                }
            });
        } else {
            Object.values(videoRefs.current).forEach(video => {
                if (video) {
                    video.controls = true;
                }
            });
        }
    }, [expandedMedia]);

    // Initialize HLS for expanded video
    useEffect(() => {
        if (expandedMedia && post.media[expandedMedia]?.type === 'video') {
            const url = post.media[expandedMedia].url;
            
            if (isHlsUrl(url)) {
                // Use setTimeout to ensure DOM element is ready
                const timeoutId = setTimeout(() => {
                    if (expandedVideoRef.current) {
                        initializeHls(expandedVideoRef.current, url, `expanded-${expandedMedia}`);
                        
                        // Try to autoplay after HLS is initialized
                        setTimeout(() => {
                            if (expandedVideoRef.current) {
                                expandedVideoRef.current.play().catch(err => {
                                    console.error('Failed to autoplay:', err);
                                });
                            }
                        }, 300);
                    }
                }, 100);
                
                return () => {
                    clearTimeout(timeoutId);
                    destroyHls(`expanded-${expandedMedia}`);
                };
            }
        }
    }, [expandedMedia, post.media]);

    // Global cleanup on unmount
    useEffect(() => {
        return () => {
            Object.keys(hlsInstances.current).forEach(mediaId => {
                if (hlsInstances.current[mediaId]) {
                    hlsInstances.current[mediaId].destroy();
                }
            });
            hlsInstances.current = {};
        };
    }, []);

    return (
        <div className="post-media">
            {mediaKeys.map((mediaId, index) => {
                const media = post.media[mediaId];
                return (
                    <div
                        style={media.type === "image" ? { backgroundImage: `url(${media.url})` } : { background: "black" }}
                        key={mediaId}
                        className={`media-container ${index === activeIndex ? "active" : ""}`}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={() => handleTouchEnd(false)}
                    >
                        <div
                            className="media-wrapper"
                            onClick={(e) => handleMediaClick(mediaId, index, e)}
                        >
                            {media.type === "image" ? (
                                <img
                                    src={media.url}
                                    alt="Post Media"
                                    className="media"
                                    onLoad={(e) => e.target.classList.add("loaded")}
                                    loading="lazy"
                                    decoding="async"
                                />
                            ) : (
                                <video
                                    poster={media?.thumbnail || ''}
                                    ref={el => {
                                        if (el) {
                                            videoRefs.current[mediaId] = el;
                                            
                                            // Only set src for non-HLS videos immediately
                                            // HLS will be initialized on interaction
                                            if (!isHlsUrl(media.url)) {
                                                el.src = media.url;
                                            }
                                        }
                                    }}
                                    controls
                                    controlsList="nofullscreen"
                                    className="media video-media"
                                    onLoadedMetadata={(e) => e.target.classList.add("loaded")}
                                    onPlay={(e) => {
                                        // Initialize HLS on first play attempt
                                        const video = e.target;
                                        if (isHlsUrl(media.url) && !hlsInstances.current[mediaId]) {
                                            initializeHls(video, media.url, mediaId);
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                );
            })}

            {expandedMedia && (
                <div className="media-overlay">
                    <div className="overlay-background"></div>
                    
                    <div className="overlay-header">
                        {mediaKeys?.length > 1 && (
                            <div className="media-position">
                                {expandedIndex + 1} / {mediaKeys?.length}
                            </div>
                        )}
                        <button className="close-button" onClick={closeExpandedView}>×</button>
                    </div>

                    <div
                        className={`expanded-media-container ${isMobile ? 'mobile-view' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={() => handleTouchEnd(true)}
                    >
                        <div className="expanded-media-wrapper">
                            {post.media[expandedMedia].type === "image" ? (
                                <img
                                    src={post.media[expandedMedia].url}
                                    alt="Expanded Media"
                                    className="expanded-media"
                                />
                            ) : (
                                <video
                                    ref={el => {
                                        if (el) {
                                            expandedVideoRef.current = el;
                                            const url = post.media[expandedMedia].url;
                                            // Only set src for non-HLS videos
                                            if (!isHlsUrl(url)) {
                                                el.src = url;
                                            }
                                        }
                                    }}
                                    controls
                                    autoPlay
                                    poster={post.media[expandedMedia]?.thumbnail || ''}
                                    className="expanded-media video-expanded"
                                    key={`expanded-${expandedMedia}`}
                                    onEnded={() => {
                                        if (expandedVideoRef.current) {
                                            const url = post.media[expandedMedia].url;
                                            if (isHlsUrl(url)) {
                                                destroyHls(`expanded-${expandedMedia}`);
                                            }
                                            expandedVideoRef.current.src = "";
                                        }
                                    }}
                                />
                            )}
                        </div>

                        {mediaKeys?.length > 1 && (
                            <>
                                <button
                                    className="expanded-nav-button prev"
                                    onClick={(e) => navigateExpandedMedia('prev', e)}
                                    disabled={expandedIndex === 0}
                                    aria-label="Previous media"
                                >
                                    &#10094;
                                </button>
                                <button
                                    className="expanded-nav-button next"
                                    onClick={(e) => navigateExpandedMedia('next', e)}
                                    disabled={expandedIndex === mediaKeys?.length - 1}
                                    aria-label="Next media"
                                >
                                    &#10095;
                                </button>

                                <div className="media-indicator">
                                    {mediaKeys.map((_, idx) => (
                                        <span
                                            key={idx}
                                            className={`indicator-dot ${idx === expandedIndex ? 'active' : ''}`}
                                            onClick={() => {
                                                setExpandedIndex(idx);
                                                setExpandedMedia(mediaKeys[idx]);
                                            }}
                                        ></span>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {mediaKeys?.length > 1 && !expandedMedia && (
                <>
                    <button
                        className="carousel-button prev"
                        onClick={() => onCarousalButtonClick(postId, mediaKeys, "prev")}
                        disabled={activeIndex === 0}
                        aria-label="Previous image"
                    >
                        &#10094;
                    </button>
                    <button
                        className="carousel-button next"
                        onClick={() => onCarousalButtonClick(postId, mediaKeys, "next")}
                        disabled={activeIndex === mediaKeys?.length - 1}
                        aria-label="Next image"
                    >
                        &#10095;
                    </button>

                    <div className="carousel-position">
                        {activeIndex + 1} / {mediaKeys?.length}
                    </div>

                    <div className="carousel-indicator">
                        {mediaKeys.map((_, idx) => (
                            <span
                                key={idx}
                                className={`carousel-dot ${idx === activeIndex ? 'active' : ''}`}
                                onClick={() => onCarousalButtonClick(postId, mediaKeys, null, idx)}
                            ></span>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default PostMedia;